import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RoleAssignmentSummarySchema, UserSummarySchema } from '@taqon/contracts';
import { z } from 'zod';
import Link from '@mui/material/Link';
import { Link as RouterLink, useNavigate } from 'react-router';
import { getData, getPage, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';

export const USERS = '/api/v1/platform/users';

export const assignmentsKey = (userId: string) => ['platform', 'users', userId, 'assignments'];

/**
 * User administration (SDD §11.3, §11.5; FRD-USR-005, USR-013): the users with their current
 * roles, each opening its own page (`/admin/users/:id`) where access is managed, and creating a
 * user. Every operation is a privileged, audited action checked by the API on each request.
 */
export function UsersPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'platform.app_user:create');
  const canSeeAssignments = hasCapability(session.data, 'platform.role_assignment:view');
  const [creating, setCreating] = useState(false);

  const users = useQuery({
    queryKey: ['platform', 'users'],
    queryFn: () => getPage(`${USERS}?pageSize=200`, UserSummarySchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Users"
        description="People who can sign in. Access comes only from the roles assigned to them."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setCreating(true);
              }}
            >
              New user
            </Button>
          ) : undefined
        }
      />
      {users.isError && <ErrorState message={users.error.message} />}
      <Section>
        {users.isPending && <LoadingState label="Loading users" />}
        {users.data !== undefined && users.data.items.length === 0 && (
          <EmptyState title="No users yet" />
        )}
        {users.data !== undefined && users.data.items.length > 0 && (
          <Table size="small" aria-label="Users">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                {canSeeAssignments && <TableCell>Current roles</TableCell>}
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.data.items.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <PersonName
                      name={user.displayName}
                      employeeId={user.employeeId}
                      userId={user.id}
                    >
                      <Link
                        component={RouterLink}
                        to={`/admin/users/${user.id}`}
                        sx={{ fontWeight: 600 }}
                      >
                        {user.displayName}
                      </Link>
                    </PersonName>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  {canSeeAssignments && (
                    <TableCell>
                      <CurrentRoles userId={user.id} />
                    </TableCell>
                  )}
                  <TableCell>
                    <StatusChip status={user.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <CreateUserDialog
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
      />
    </Stack>
  );
}

export function useAssignments(userId: string, enabled = true) {
  return useQuery({
    queryKey: assignmentsKey(userId),
    enabled,
    queryFn: () =>
      getData(`${USERS}/${userId}/role-assignments`, z.array(RoleAssignmentSummarySchema)),
  });
}

function CurrentRoles({ userId }: { userId: string }) {
  const assignments = useAssignments(userId);
  if (assignments.isPending) return <Typography variant="body2">…</Typography>;
  if (assignments.isError) return <Typography variant="body2">—</Typography>;
  const current = assignments.data.filter((a) => a.current);
  if (current.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No role
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {current.map((a) => (
        <Chip
          key={a.id}
          size="small"
          label={a.roleName}
          color={a.isPrivileged ? 'primary' : 'default'}
          variant="outlined"
        />
      ))}
    </Stack>
  );
}

function CreateUserDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const create = useMutation({
    mutationFn: () =>
      postJson(
        USERS,
        { email: email.trim(), displayName: displayName.trim() },
        UserSummarySchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async (created) => {
      setEmail('');
      setDisplayName('');
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['platform', 'users'] });
      // The new user opens on its own page, ready for its roles and employee link.
      await navigate(`/admin/users/${created.id}`);
    },
  });
  return (
    <FormDialog
      open={open}
      title="New user"
      submitLabel="Create user"
      canSubmit={email.trim() !== '' && displayName.trim() !== ''}
      submitting={create.isPending}
      error={create.isError ? create.error.message : null}
      onClose={() => {
        create.reset();
        onClose();
      }}
      onSubmit={() => {
        create.mutate();
      }}
    >
      <TextField
        label="Email"
        type="email"
        required
        autoFocus
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
        }}
        helperText="The address the person signs in with."
      />
      <TextField
        label="Display name"
        required
        value={displayName}
        onChange={(e) => {
          setDisplayName(e.target.value);
        }}
      />
      <Alert severity="info" variant="outlined">
        A new user has no access until a role is assigned.
      </Alert>
    </FormDialog>
  );
}
